import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Plus, Trash, UploadSimple, Printer, PencilSimple, Users, UserPlus, X } from "@phosphor-icons/react";
import { humanizeAxiosError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

export default function NormCadre() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [petitions, setPetitions] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [petitionOpen, setPetitionOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ department: "", unit: "", position: "", target_count: 0, current_count: "", notes: "" });
  const [petForm, setPetForm] = useState({ department: "", position: "", requested_count: 1, reason: "" });
  const [petFile, setPetFile] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [printData, setPrintData] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSelect, setAssignSelect] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);

  const canManage = ["admin", "hr", "gm"].includes(user?.role);
  const canPetition = ["manager", "admin", "hr"].includes(user?.role);
  const isHR = ["hr", "admin"].includes(user?.role);
  const isGM = ["gm", "admin"].includes(user?.role);

  const load = () => {
    api.get("/norm-cadre").then((r) => setItems(r.data)).catch(() => {});
    api.get("/cadre-petitions").then((r) => setPetitions(r.data)).catch(() => {});
    api.get("/personnel").then((r) => setPersonnel(r.data));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ department: user?.role === "manager" ? user.department : "", unit: "", position: "", target_count: 0, current_count: "", notes: "" }); setDialogOpen(true); };
  const openEdit = (i) => { setEditing(i); setForm({ department: i.department, unit: i.unit || "", position: i.position, target_count: i.target_count, current_count: (i.current_count === null || i.current_count === undefined) ? "" : i.current_count, notes: i.notes || "" }); setDialogOpen(true); };
  const submit = async () => {
    if (!form.department || !form.position) return toast.error("Departman ve pozisyon zorunlu");
    try {
      const payload = {
        ...form,
        target_count: Number(form.target_count) || 0,
        current_count: form.current_count === "" ? null : Number(form.current_count),
      };
      if (editing) await api.patch(`/norm-cadre/${editing.id}`, payload);
      else await api.post("/norm-cadre", payload);
      toast.success(editing ? "Güncellendi" : "Eklendi");
      setDialogOpen(false); load();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };
  const del = async (i) => {
    if (!window.confirm(`"${i.department} — ${i.position}" kadrosunu silmek istediğinize emin misiniz?`)) return;
    await api.delete(`/norm-cadre/${i.id}`); load();
  };

  const submitPetition = async () => {
    if (!petForm.department || !petForm.position || !petForm.reason) return toast.error("Tüm alanlar zorunlu");
    try {
      const fd = new FormData();
      fd.append("department", petForm.department);
      fd.append("position", petForm.position);
      fd.append("requested_count", String(petForm.requested_count));
      fd.append("reason", petForm.reason);
      if (petFile) fd.append("file", petFile);
      await api.post("/cadre-petitions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dilekçe gönderildi");
      setPetitionOpen(false); setPetForm({ department: "", position: "", requested_count: 1, reason: "" }); setPetFile(null); load();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const hrApprovePet = async (p) => { try { await api.post(`/cadre-petitions/${p.id}/hr-approve`, {}); toast.success("İK onayı"); load(); } catch (e) { toast.error(humanizeAxiosError(e)); } };
  const gmApprovePet = async (p) => { try { await api.post(`/cadre-petitions/${p.id}/gm-approve`, {}); toast.success("GM onayı"); load(); } catch (e) { toast.error(humanizeAxiosError(e)); } };
  const doReject = async () => {
    if (!rejectReason) return;
    try { await api.post(`/cadre-petitions/${rejectFor.id}/reject`, { reason: rejectReason }); toast.success("Reddedildi"); setRejectFor(null); setRejectReason(""); load(); }
    catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const departments = [...new Set(personnel.map((p) => p.department).filter(Boolean))];

  const fetchAssignees = async (cid) => {
    setAssignLoading(true);
    try {
      const { data } = await api.get(`/norm-cadre/${cid}/assignments`);
      setAssignees(data || []);
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setAssignLoading(false); }
  };
  const openAssign = (i) => { setAssignFor(i); setAssignees([]); setAssignSelect(""); fetchAssignees(i.id); };
  const doAssign = async () => {
    if (!assignSelect) return toast.error("Personel seçin");
    setAssignBusy(true);
    try {
      await api.post(`/norm-cadre/${assignFor.id}/assign`, { user_id: assignSelect });
      toast.success("Atandı");
      setAssignSelect("");
      await fetchAssignees(assignFor.id);
      load();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setAssignBusy(false); }
  };
  const doUnassign = async (uid) => {
    setAssignBusy(true);
    try {
      await api.delete(`/norm-cadre/${assignFor.id}/assign/${uid}`);
      toast.success("Çıkarıldı");
      await fetchAssignees(assignFor.id);
      load();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setAssignBusy(false); }
  };
  const assignedIds = new Set(assignees.map((a) => a.id));
  const assignablepeople = personnel
    .filter((p) => p.status === "approved" && p.is_active !== false && !assignedIds.has(p.id))
    .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, "tr"));

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">İnsan Kaynakları</div>
        <h1 className="font-display text-3xl font-bold mt-1">Norm Kadro</h1>
      </div>

      <Tabs defaultValue="cadre">
        <TabsList>
          <TabsTrigger value="cadre" data-testid="tab-cadre">Kadro Tablosu</TabsTrigger>
          <TabsTrigger value="petitions" data-testid="tab-petitions">Dilekçeler ({petitions.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="cadre">
          <div className="flex justify-end mb-3 gap-2">
            {canManage && <Button onClick={openNew} data-testid="new-cadre"><Plus size={14} className="mr-1" />Kadro Ekle</Button>}
            {canPetition && <Button variant="outline" onClick={() => { setPetForm({ department: user?.role === "manager" ? user.department : "", position: "", requested_count: 1, reason: "" }); setPetitionOpen(true); }} data-testid="new-petition"><UploadSimple size={14} className="mr-1" />Dilekçe Ver</Button>}
          </div>
          <Card>
            <CardContent className="p-0">
              {(() => {
                if (items.length === 0) {
                  return <div className="p-8 text-center text-muted-foreground text-sm">Norm kadro tanımlanmamış</div>;
                }
                // Group items by department, then by unit within each department.
                // Empty unit is bucketed as "Genel" so managers still see a clean row.
                const byDept = {};
                for (const it of items) {
                  const dep = it.department || "-";
                  const unit = (it.unit || "").trim() || "Genel";
                  byDept[dep] = byDept[dep] || {};
                  byDept[dep][unit] = byDept[dep][unit] || [];
                  byDept[dep][unit].push(it);
                }
                const sumRows = (rows) => rows.reduce(
                  (acc, r) => {
                    acc.target += Number(r.target_count) || 0;
                    acc.actual += Number(r.actual_count) || 0;
                    return acc;
                  },
                  { target: 0, actual: 0 }
                );
                const gapBadge = (gap) =>
                  gap > 0 ? <Badge variant="destructive">Eksik: {gap}</Badge>
                  : gap < 0 ? <Badge variant="warning">Fazla: {Math.abs(gap)}</Badge>
                  : <Badge>Tam</Badge>;

                const deptNames = Object.keys(byDept).sort((a, b) => a.localeCompare(b, "tr"));

                return (
                  <Accordion type="multiple" defaultValue={deptNames} className="w-full" data-testid="cadre-accordion">
                    {deptNames.map((dep) => {
                      const units = byDept[dep];
                      const allRows = Object.values(units).flat();
                      const { target: dT, actual: dA } = sumRows(allRows);
                      const dGap = dT - dA;
                      const unitNames = Object.keys(units).sort((a, b) => {
                        // "Genel" always last so named units show first
                        if (a === "Genel") return 1;
                        if (b === "Genel") return -1;
                        return a.localeCompare(b, "tr");
                      });
                      return (
                        <AccordionItem key={dep} value={dep} className="border-b border-border last:border-b-0" data-testid={`cadre-dept-${dep}`}>
                          <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30">
                            <div className="flex-1 flex items-center justify-between gap-3 pr-3">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{dep}</span>
                                <span className="text-xs text-muted-foreground">
                                  {Object.keys(units).length} birim · {allRows.length} satır
                                </span>
                              </div>
                              <div className="hidden sm:flex items-center gap-3 text-xs">
                                <span>Hedef: <b>{dT}</b></span>
                                <span>Mevcut: <b>{dA}</b></span>
                                {gapBadge(dGap)}
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-0 pb-0">
                            {unitNames.map((unitName) => {
                              const rows = units[unitName];
                              const { target: uT, actual: uA } = sumRows(rows);
                              const uGap = uT - uA;
                              return (
                                <div key={unitName} className="border-t border-border/60" data-testid={`cadre-unit-${dep}-${unitName}`}>
                                  <div className="flex items-center justify-between gap-3 px-6 py-2 bg-muted/20 text-xs">
                                    <div className="uppercase tracking-wider text-muted-foreground">
                                      <span className="opacity-60 mr-1">Birim:</span>
                                      <b className="text-foreground">{unitName}</b>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span>Hedef: <b>{uT}</b></span>
                                      <span>Mevcut: <b>{uA}</b></span>
                                      {gapBadge(uGap)}
                                    </div>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead className="bg-background text-[10px] uppercase tracking-wider text-muted-foreground">
                                        <tr>
                                          <th className="text-left px-6 py-2 font-medium">Pozisyon</th>
                                          <th className="text-left px-3 py-2 font-medium">Hedef</th>
                                          <th className="text-left px-3 py-2 font-medium">Mevcut</th>
                                          <th className="text-left px-3 py-2 font-medium">Fark</th>
                                          <th className="text-left px-3 py-2 font-medium">Notlar</th>
                                          {canManage && <th className="text-left px-3 py-2 font-medium">İşlem</th>}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {rows.map((i) => (
                                          <tr key={i.id} className="border-t border-border/50">
                                            <td className="px-6 py-2">
                                              <button
                                                type="button"
                                                onClick={() => openAssign(i)}
                                                className="text-left inline-flex items-center gap-1.5 hover:text-primary hover:underline font-medium"
                                                data-testid={`cadre-pos-${i.id}`}
                                                title="Atanan personeli görüntüle / düzenle"
                                              >
                                                {i.position}
                                                <Badge variant="secondary" className="ml-1 text-[10px]">
                                                  <Users size={11} className="mr-0.5" />{i.assigned_count || 0}
                                                </Badge>
                                              </button>
                                            </td>
                                            <td className="px-3 py-2">{i.target_count}</td>
                                            <td className="px-3 py-2">
                                              {i.actual_count}
                                              {i.current_count !== null && i.current_count !== undefined && (
                                                <span className="ml-1 text-[10px] text-muted-foreground" title="Manuel girildi">✎</span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2">
                                              {typeof i.gap !== "number" ? (
                                                <span className="text-muted-foreground text-xs">—</span>
                                              ) : i.gap > 0 ? (
                                                <Badge variant="destructive" data-testid={`gap-${i.id}`}>Eksik: {i.gap}</Badge>
                                              ) : i.gap < 0 ? (
                                                <Badge variant="warning" data-testid={`gap-${i.id}`}>Fazla: {Math.abs(i.gap)}</Badge>
                                              ) : (
                                                <Badge data-testid={`gap-${i.id}`}>Tam</Badge>
                                              )}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-muted-foreground">{i.notes}</td>
                                            {canManage && (
                                              <td className="px-3 py-2">
                                                <div className="flex gap-1">
                                                  <Button size="icon" variant="ghost" onClick={() => openEdit(i)} data-testid={`edit-cadre-${i.id}`}><PencilSimple size={14} /></Button>
                                                  <Button size="icon" variant="ghost" onClick={() => del(i)} data-testid={`del-cadre-${i.id}`}><Trash size={14} className="text-destructive" /></Button>
                                                </div>
                                              </td>
                                            )}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            })}
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="petitions">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-left p-3">Talep Eden</th>
                      <th className="text-left p-3">Departman</th>
                      <th className="text-left p-3">Pozisyon</th>
                      <th className="text-left p-3">Adet</th>
                      <th className="text-left p-3">Dosya</th>
                      <th className="text-left p-3">Durum</th>
                      <th className="text-left p-3">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {petitions.map((p) => (
                      <tr key={p.id} className="border-t border-border">
                        <td className="p-3">{p.requested_by_name}<div className="text-[11px] text-muted-foreground">{p.requested_at?.slice(0,10)}</div></td>
                        <td className="p-3">{p.department}</td>
                        <td className="p-3">{p.position}</td>
                        <td className="p-3">{p.requested_count}</td>
                        <td className="p-3">{p.file_url ? <a href={`${API}${p.file_url}`} target="_blank" rel="noreferrer" className="text-primary underline text-xs">{p.file_name || "İndir"}</a> : <span className="text-xs text-muted-foreground">-</span>}</td>
                        <td className="p-3">
                          <div className="flex gap-1 flex-col items-start">
                            <Badge variant={p.final_status === "approved" ? "default" : (p.final_status === "rejected" ? "destructive" : "secondary")}>{p.final_status === "approved" ? "Onaylandı" : (p.final_status === "rejected" ? "Reddedildi" : "Beklemede")}</Badge>
                            <div className="flex gap-1 text-[10px]">
                              <span className={p.hr_status === "approved" ? "text-primary" : "text-muted-foreground"}>İK</span>
                              <span className={p.gm_status === "approved" ? "text-primary" : "text-muted-foreground"}>GM</span>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1 flex-wrap">
                            {isHR && p.hr_status !== "approved" && p.final_status === "pending" && (
                              <Button size="sm" variant="outline" onClick={() => hrApprovePet(p)} data-testid={`pet-hr-${p.id}`}>İK Onayla</Button>
                            )}
                            {isGM && p.hr_status === "approved" && p.gm_status !== "approved" && p.final_status === "pending" && (
                              <Button size="sm" onClick={() => gmApprovePet(p)} data-testid={`pet-gm-${p.id}`}>GM Onayla</Button>
                            )}
                            {(isHR || isGM) && p.final_status === "pending" && (
                              <Button size="sm" variant="ghost" onClick={() => { setRejectFor(p); setRejectReason(""); }} data-testid={`pet-reject-${p.id}`}>Reddet</Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => setPrintData(p)}><Printer size={14} /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {petitions.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Dilekçe yok</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Kadro Düzenle" : "Yeni Norm Kadro"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Departman</Label>
              <Select value={form.department} onValueChange={(v) => setForm({...form, department: v, unit: ""})} disabled={user?.role === "manager"}>
                <SelectTrigger data-testid="cadre-dept"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Birim <span className="text-xs text-muted-foreground font-normal">(opsiyonel — örn. "Soğuk Mutfak")</span></Label>
              <Input
                value={form.unit}
                onChange={(e) => setForm({...form, unit: e.target.value})}
                placeholder="Alt birim adı"
                list="cadre-unit-suggestions"
                data-testid="cadre-unit"
              />
              <datalist id="cadre-unit-suggestions">
                {[...new Set(items.filter((it) => it.department === form.department && it.unit).map((it) => it.unit))].map((u) => (
                  <option key={u} value={u} />
                ))}
              </datalist>
            </div>
            <div><Label>Pozisyon</Label><Input value={form.position} onChange={(e) => setForm({...form, position: e.target.value})} data-testid="cadre-position" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Hedef Sayı</Label><Input type="number" min={0} value={form.target_count} onChange={(e) => setForm({...form, target_count: Number(e.target.value)})} data-testid="cadre-target" /></div>
              <div>
                <Label>Mevcut Sayı <span className="text-xs text-muted-foreground font-normal">(boş = otomatik)</span></Label>
                <Input
                  type="number"
                  min={0}
                  value={form.current_count}
                  onChange={(e) => setForm({...form, current_count: e.target.value})}
                  placeholder="Otomatik"
                  data-testid="cadre-current"
                />
              </div>
            </div>
            {(() => {
              // Live preview: departman + (varsa) birim + (varsa) pozisyon bazında mevcut/hedef/fark.
              const dep = (form.department || "").trim().toLowerCase();
              if (!dep) return null;
              const activePeople = personnel.filter(
                (p) => (p.is_active !== false) && (p.status === "approved")
              );
              const deptCurrent = activePeople.filter(
                (p) => (p.department || "").trim().toLowerCase() === dep
              ).length;
              const deptTarget = items
                .filter((it) => (it.department || "").trim().toLowerCase() === dep && (!editing || it.id !== editing.id))
                .reduce((s, it) => s + (Number(it.target_count) || 0), 0)
                + Number(form.target_count || 0);
              const deptGap = deptTarget - deptCurrent;

              const unitName = (form.unit || "").trim();
              const unitLower = unitName.toLowerCase();
              // Birim mevcudu: manuel current_count varsa onu topla; yoksa pozisyon eşleşen aktif personel sayısını topla.
              const unitPeers = items.filter(
                (it) => (it.department || "").trim().toLowerCase() === dep
                     && (it.unit || "").trim().toLowerCase() === unitLower
                     && (!editing || it.id !== editing.id)
              );
              const unitTargetOther = unitPeers.reduce((s, it) => s + (Number(it.target_count) || 0), 0);
              const unitTarget = unitTargetOther + Number(form.target_count || 0);

              const pos = (form.position || "").trim().toLowerCase();
              const posAutoCurrent = pos ? activePeople.filter(
                (p) => (p.department || "").trim().toLowerCase() === dep &&
                       (p.position || "").trim().toLowerCase() === pos
              ).length : 0;
              const posManualCurrent = form.current_count !== "" ? Number(form.current_count) : null;
              const posCurrent = pos ? (posManualCurrent !== null ? posManualCurrent : posAutoCurrent) : null;
              const posTarget = pos ? Number(form.target_count || 0) : null;
              const posGap = pos ? (posTarget - posCurrent) : null;

              const badge = (gap) => gap > 0
                ? <b className="text-destructive">Eksik {gap}</b>
                : gap < 0 ? <b className="text-amber-600">Fazla {Math.abs(gap)}</b>
                          : <b className="text-emerald-700">Tam</b>;

              return (
                <div className="rounded-md border border-border bg-muted/40 p-3 text-xs space-y-2" data-testid="cadre-live-preview">
                  <div className="uppercase tracking-widest text-[10px] text-muted-foreground font-semibold">Anlık Hesap</div>
                  <div data-testid="cadre-preview-dept">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Departman: {form.department}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                      <span>Mevcut: <b>{deptCurrent}</b></span>
                      <span>Hedef: <b>{deptTarget}</b></span>
                      <span>Fark (Hedef - Mevcut): {badge(deptGap)}</span>
                    </div>
                  </div>
                  {unitName && (
                    <div data-testid="cadre-preview-unit" className="pt-2 border-t border-border/60">
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Birim: {unitName}</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                        <span>Hedef Toplamı: <b>{unitTarget}</b></span>
                        <span className="text-muted-foreground italic">Mevcut, birim bazında bu satır için elle girilebilir</span>
                      </div>
                    </div>
                  )}
                  {pos && (
                    <div data-testid="cadre-preview-pos" className="pt-2 border-t border-border/60">
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Pozisyon: {form.position}</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                        <span>Mevcut: <b>{posCurrent}</b>{posManualCurrent !== null && <span className="ml-1 text-muted-foreground">(elle)</span>}</span>
                        <span>Hedef: <b>{posTarget}</b></span>
                        <span>Fark: {badge(posGap)}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            <div><Label>Notlar</Label><Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Vazgeç</Button>
            <Button onClick={submit} data-testid="cadre-submit">{editing ? "Güncelle" : "Ekle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={petitionOpen} onOpenChange={setPetitionOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Yeni Kadro Dilekçesi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Departman</Label>
              <Select value={petForm.department} onValueChange={(v) => setPetForm({...petForm, department: v})} disabled={user?.role === "manager"}>
                <SelectTrigger data-testid="pet-dept"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Pozisyon</Label><Input value={petForm.position} onChange={(e) => setPetForm({...petForm, position: e.target.value})} data-testid="pet-position" /></div>
              <div><Label>Talep Edilen Adet</Label><Input type="number" min={1} value={petForm.requested_count} onChange={(e) => setPetForm({...petForm, requested_count: Number(e.target.value)})} data-testid="pet-count" /></div>
            </div>
            <div><Label>Gerekçe</Label><Textarea value={petForm.reason} onChange={(e) => setPetForm({...petForm, reason: e.target.value})} data-testid="pet-reason" rows={4} /></div>
            <div>
              <Label>İmzalı Dilekçe (opsiyonel, PDF/DOC/DOCX/JPG/PNG)</Label>
              <Input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => setPetFile(e.target.files?.[0])} data-testid="pet-file" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPetitionOpen(false)}>Vazgeç</Button>
            <Button onClick={submitPetition} data-testid="pet-submit">Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Dilekçe Reddi</DialogTitle></DialogHeader>
          <Label>Ret gerekçesi</Label>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="pet-reject-reason" />
          <Button variant="destructive" onClick={doReject} disabled={!rejectReason} data-testid="pet-reject-submit">Reddet</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users size={18} /> {assignFor?.position} — Atanan Personel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground">
              {assignFor?.department}{assignFor?.unit ? ` · ${assignFor.unit}` : ""} · Hedef: <b>{assignFor?.target_count}</b> · Atanan: <b>{assignees.length}</b>
            </div>

            {canManage && (
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>Personel Ekle</Label>
                  <Select value={assignSelect} onValueChange={setAssignSelect}>
                    <SelectTrigger data-testid="assign-select"><SelectValue placeholder="Personel seçin" /></SelectTrigger>
                    <SelectContent>
                      {assignablepeople.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground">Eklenebilecek personel yok</div>}
                      {assignablepeople.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.first_name} {p.last_name} — {p.department || "-"} ({p.position || "-"})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={doAssign} disabled={!assignSelect || assignBusy} data-testid="assign-add-btn">
                  <UserPlus size={15} className="mr-1" />Ata
                </Button>
              </div>
            )}

            <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="assignees-list">
              {assignLoading && <div className="text-sm text-muted-foreground py-4 text-center">Yükleniyor…</div>}
              {!assignLoading && assignees.length === 0 && (
                <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-md">
                  Bu pozisyona atanmış personel yok
                </div>
              )}
              {assignees.map((a) => (
                <div key={a.id} className="flex items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2" data-testid={`assignee-${a.id}`}>
                  <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden shrink-0">
                    {a.photo_url ? (
                      <img src={a.photo_url.startsWith("http") ? a.photo_url : `${API}${a.photo_url}`} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-xs font-semibold text-primary">{(a.first_name?.[0] || "") + (a.last_name?.[0] || "")}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{a.first_name} {a.last_name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{a.department || "-"} · {a.position || "-"}</div>
                  </div>
                  {canManage && (
                    <Button size="icon" variant="ghost" onClick={() => doUnassign(a.id)} disabled={assignBusy} data-testid={`assignee-remove-${a.id}`} title="Çıkar">
                      <X size={15} className="text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!printData} onOpenChange={(o) => !o && setPrintData(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Kadro Dilekçesi</DialogTitle></DialogHeader>
          {printData && (
            <div>
              <div className="printable bg-white text-black p-8 rounded border border-border">
                <div className="text-center mb-6">
                  <div className="font-display text-2xl font-bold">COLOSSAE HOTEL</div>
                  <div className="text-sm uppercase tracking-[0.3em]">Norm Kadro Talep Dilekçesi</div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><b>Talep Eden:</b> {printData.requested_by_name}</div>
                  <div><b>Departman:</b> {printData.department}</div>
                  <div><b>Pozisyon:</b> {printData.position}</div>
                  <div><b>Talep Edilen Adet:</b> {printData.requested_count}</div>
                  <div className="col-span-2"><b>Gerekçe:</b> {printData.reason}</div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-8 border-t pt-4">
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">Müdür İmzası</div>
                    {printData.manager_signature && <img src={printData.manager_signature} className="h-16" alt="mgr" />}
                    <div className="text-[10px] text-muted-foreground">{printData.requested_at?.slice(0,16).replace('T',' ')}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">İK İmzası</div>
                    {printData.hr_signature && <img src={printData.hr_signature} className="h-16" alt="hr" />}
                    <div className="text-[10px] text-muted-foreground">{printData.hr_signed_at?.slice(0,16).replace('T',' ') || ""}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">Genel Müdür İmzası</div>
                    {printData.gm_signature && <img src={printData.gm_signature} className="h-16" alt="gm" />}
                    <div className="text-[10px] text-muted-foreground">{printData.gm_signed_at?.slice(0,16).replace('T',' ') || ""}</div>
                  </div>
                </div>
                {printData.final_status === "rejected" && printData.reject_reason && (
                  <div className="mt-4 p-3 border border-destructive/40 bg-destructive/10 rounded">
                    <div className="text-xs font-semibold text-destructive">Ret Gerekçesi</div>
                    <div className="text-sm">{printData.reject_reason}</div>
                  </div>
                )}
              </div>
              <div className="flex justify-end mt-4"><Button onClick={() => window.print()}><Printer size={16} className="mr-1" />Yazdır</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
